import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { ConnectionManagerNetworkKeys } from '~/helpers/constants';
import { ConnectionItemShape } from '../../helpers/propTypeShapes';
import MoreOptionsButton from '../MoreOptionsButton';

export default function ConnectionTableRowRenderer({
  item: connectionItem,
  onEditOption,
  onRemoveOption,
  onRenameOption,
}) {
  const {
    title,
    enabled,
    socialiveAvailable,
    network: { logo, title: networkTitle, vendor, type: integrationType } = {},
  } = connectionItem;

  const [isSelected, setIsSelected] = useState(false);
  const networkVendor = useMemo(() => vendor ?? networkTitle, [vendor, networkTitle]);

  const handleOnEdit = useCallback(() => {
    onEditOption(connectionItem);
    setIsSelected(false);
  }, [onEditOption, connectionItem]);

  const handleOnRemove = useCallback(() => {
    onRemoveOption(connectionItem);
    setIsSelected(false);
  }, [onRemoveOption, connectionItem]);

  const handleOnRename = useCallback(() => {
    onRenameOption(connectionItem);
    setIsSelected(false);
  }, [onRenameOption, connectionItem]);

  const defaultOptionItems = useMemo(
    () => [
      {
        title: 'Edit integration',
        icon: <i className="icon-pencil-edit circle" />,
        onClickHandler: handleOnEdit,
      },
      {
        title: 'Remove integration',
        icon: <i className="icon-delete-circle" />,
        classModifier: 'highlight-remove-color',
        onClickHandler: handleOnRemove,
      },
    ],
    [handleOnEdit, handleOnRemove]
  );

  const trayOptionItems = useMemo(
    () => [
      {
        title: 'Edit integration',
        icon: <i className="icon-pencil-edit circle" />,
        onClickHandler: handleOnEdit,
      },
      {
        title: 'Rename integration',
        icon: <i className="icon-pencil-edit circle" />,
        onClickHandler: handleOnRename,
      },
      {
        title: 'Remove integration',
        icon: <i className="icon-delete-circle" />,
        classModifier: 'highlight-remove-color',
        onClickHandler: handleOnRemove,
      },
    ],
    [handleOnEdit, handleOnRemove, handleOnRename]
  );

  return (
    <tr className={classnames('body-row connection-manager', { selected: isSelected })}>
      <td className="body-column">
        <div className={classnames('body-column-name', { disabled: !socialiveAvailable || !enabled })}>
          {socialiveAvailable ? (
            <img className="logo" src={logo} alt={`${networkVendor} logo`} />
          ) : (
            <div className="logo">
              <i className="icon-alert-triangle" />
            </div>
          )}
          <div>
            {socialiveAvailable ? (
              <button type="button" className="title" onClick={handleOnEdit}>
                {title}
              </button>
            ) : (
              <p className="title">{title}</p>
            )}

            <p className="description text-uppercase">{networkVendor}</p>
          </div>
        </div>
      </td>
      <td className="body-column status">
        <div className="body-column-error">
          {!socialiveAvailable ? (
            <p>No longer available - please remove</p>
          ) : (
            !enabled && <p>Connection disabled - please edit or remove</p>
          )}
        </div>
      </td>
      <td className="body-column">
        <div className="body-column-actions">
          <MoreOptionsButton
            options={
              enabled && integrationType === ConnectionManagerNetworkKeys.TRAY ? trayOptionItems : defaultOptionItems
            }
            isPopperOpen={isSelected}
            onTogglePopper={(isOpen) => setIsSelected(isOpen)}
          />
        </div>
      </td>
    </tr>
  );
}

ConnectionTableRowRenderer.propTypes = {
  item: ConnectionItemShape.isRequired,
  onEditOption: PropTypes.func.isRequired,
  onRenameOption: PropTypes.func.isRequired,
  onRemoveOption: PropTypes.func.isRequired,
};
